// apps/web/src/lib/compras/notas.ts — Fase 13, Plano 22 (COMP-03, D-15, D-17).
// Lista "Notas de Compra": apresentação da situação da NF (paleta fechada do UI-SPEC), filtros espelhados na URL e a query
// do servidor. Nada é calculado aqui: situação, pedidos atendidos e quem recebeu vêm do servidor.
import type { AbaNotasCompra, FornecedorLookup, SituacaoNotaCompra } from '@pieltec/contracts';
import { SITUACOES_NOTA_COMPRA } from '@pieltec/contracts';
import { ehUuid } from './uuid';

export type IconeSituacaoNota = 'FileClock' | 'CheckCircle2' | 'Undo2';

export interface ApresentacaoSituacaoNota {
  readonly rotulo: string;
  readonly icone: IconeSituacaoNota;
  readonly classes: string;
}

const APRESENTACAO: Record<SituacaoNotaCompra, ApresentacaoSituacaoNota> = {
  A_CONFERIR: { rotulo: 'A conferir', icone: 'FileClock', classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  RECEBIDA: { rotulo: 'Recebida', icone: 'CheckCircle2', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  DEVOLVIDA_PARCIAL: { rotulo: 'Devolvida em parte', icone: 'Undo2', classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  DEVOLVIDA: { rotulo: 'Devolvida', icone: 'Undo2', classes: 'bg-slate-100 text-slate-500 border-slate-200' },
};

/** Situação desconhecida (versão nova do servidor) cai num badge neutro com o texto cru: nunca quebra a tela. */
export function apresentacaoSituacaoNota(situacao: SituacaoNotaCompra): ApresentacaoSituacaoNota {
  return APRESENTACAO[situacao] ?? { rotulo: String(situacao), icone: 'FileClock', classes: 'bg-slate-100 text-slate-500 border-slate-200' };
}

export function rotuloSituacaoNota(situacao: SituacaoNotaCompra): string {
  return apresentacaoSituacaoNota(situacao).rotulo;
}

/** Subtítulo da lista: "{N} notas" (singular para 1). */
export function contagemNotas(total: number): string {
  return `${total} ${total === 1 ? 'nota' : 'notas'}`;
}

/** Rótulo da aba "Compras sem pedido ({N})": N omitido quando 0 ou desconhecido. */
export function rotuloAbaSemPedido(total: number | null): string {
  return total !== null && total > 0 ? `Compras sem pedido (${total})` : 'Compras sem pedido';
}

export interface FiltrosNotasCompra {
  aba: AbaNotasCompra;
  fornecedor: FornecedorLookup | null;
  situacao: SituacaoNotaCompra | '';
  de: string;
  ate: string;
}

const DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;

export function filtrosNotasVazios(aba: AbaNotasCompra = 'TODAS'): FiltrosNotasCompra {
  return { aba, fornecedor: null, situacao: '', de: '', ate: '' };
}

/** Há filtro além da aba? (decide entre "vazio" e "sem resultado"). */
export function filtrosNotasAtivos(filtros: FiltrosNotasCompra): boolean {
  return filtros.fornecedor !== null || filtros.situacao !== '' || filtros.de !== '' || filtros.ate !== '';
}

/** Query string (sem `?`) para o servidor. O período só vale se as datas forem reais; De > Até volta como erro 400 do servidor. */
export function montarQueryNotasCompra(filtros: FiltrosNotasCompra, pagina: number, aba: AbaNotasCompra = filtros.aba): string {
  const params = new URLSearchParams();
  params.set('aba', aba);
  params.set('pagina', String(pagina));
  if (filtros.fornecedor) params.set('fornecedorId', filtros.fornecedor.id);
  if (filtros.situacao !== '') params.set('situacao', filtros.situacao);
  if (DATA_ISO.test(filtros.de)) params.set('de', filtros.de);
  if (DATA_ISO.test(filtros.ate)) params.set('ate', filtros.ate);
  return params.toString();
}

/** Lê aba e filtros da URL. Valor inválido volta ao padrão. */
export function lerFiltrosNotas(search: string): FiltrosNotasCompra {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const filtros = filtrosNotasVazios(params.get('aba') === 'sem-pedido' ? 'SEM_PEDIDO' : 'TODAS');
  const fornecedorId = params.get('fornecedorId');
  if (ehUuid(fornecedorId)) {
    filtros.fornecedor = {
      id: fornecedorId,
      cnpj: params.get('fornecedorCnpj') ?? '',
      razaoSocial: params.get('fornecedorNome') ?? '',
      situacao: 'ATIVO',
    };
  }
  const situacao = params.get('situacao');
  if (situacao && (SITUACOES_NOTA_COMPRA as readonly string[]).includes(situacao)) filtros.situacao = situacao as SituacaoNotaCompra;
  const de = params.get('de') ?? '';
  const ate = params.get('ate') ?? '';
  if (DATA_ISO.test(de)) filtros.de = de;
  if (DATA_ISO.test(ate)) filtros.ate = ate;
  return filtros;
}

/** Query string (sem `?`) da URL do navegador: só o que difere do padrão; a aba vai como `aba=sem-pedido`. */
export function escreverFiltrosNotas(filtros: FiltrosNotasCompra): string {
  const params = new URLSearchParams();
  if (filtros.aba === 'SEM_PEDIDO') params.set('aba', 'sem-pedido');
  if (filtros.fornecedor) {
    params.set('fornecedorId', filtros.fornecedor.id);
    if (filtros.fornecedor.razaoSocial) params.set('fornecedorNome', filtros.fornecedor.razaoSocial);
    if (filtros.fornecedor.cnpj) params.set('fornecedorCnpj', filtros.fornecedor.cnpj);
  }
  if (filtros.situacao !== '') params.set('situacao', filtros.situacao);
  if (filtros.de !== '') params.set('de', filtros.de);
  if (filtros.ate !== '') params.set('ate', filtros.ate);
  return params.toString();
}

export type EstadoListaNotas = 'erro' | 'carregando' | 'vazio' | 'sem-resultado' | 'lista';

export function estadoDaListaNotas(entrada: {
  readonly erro: boolean;
  readonly carregando: boolean;
  readonly temDados: boolean;
  readonly total: number;
  readonly comFiltro: boolean;
}): EstadoListaNotas {
  if (entrada.erro) return 'erro';
  if (entrada.carregando && !entrada.temDados) return 'carregando';
  if (entrada.total === 0) return entrada.comFiltro ? 'sem-resultado' : 'vazio';
  return 'lista';
}
